<template>
  <div class="ical-viewer_container">
    <div v-if="icalData.length" class="ical-cards">
      <div v-for="(event, index) in icalData" :key="index" class="ical-card">
        <h3 class="ical-summary">📆 {{ event.summary || "Untitled Event" }}</h3>
        <p><strong>🗓 Start: </strong> {{ event.start || "—" }}</p>
        <p><strong>🗓 End: </strong> {{ event.end || "—" }}</p>
        <p v-if="event.location"><strong>📍 Location:</strong> {{ event.location }}</p>
        <p v-if="event.description">
            <strong>📝 Description:</strong><br>
            <span v-html="formatDescription(event.description)"></span>
        </p>
        <p v-if="event.url">
          <strong>🔗 Link: </strong>
          <a :href="event.url" target="_blank">{{ event.url }}</a>
        </p>
      </div>
    </div>

    <p v-else>Loading...</p>
  </div>
</template>

<script setup>
import { ref, watchEffect } from "vue";
import { useStore } from "vuex";

const icalData = ref([]);

const props = defineProps({
  documentName: String,
});

const store = useStore();

/**
 * Parses .ics text into an array of events.
 * Handles VALUE=DATE and folded lines.
 */
const parseICS = (icsText) => {
  // Unfold lines (join ones starting with a space/tab)
  const unfolded = icsText.replace(/\r?\n[ \t]/g, "");
  const lines = unfolded.split(/\r?\n/);
  const events = [];
  let currentEvent = null;

  for (const line of lines) {
    if (line.startsWith("BEGIN:VEVENT")) {
      currentEvent = {};
    } else if (line.startsWith("END:VEVENT")) {
      if (currentEvent) events.push(currentEvent);
      currentEvent = null;
    } else if (currentEvent) {
      const [rawKey, ...valueParts] = line.split(":");
      const value = valueParts.join(":");
      if (!value) continue;

      // Handle fields like DTSTART;VALUE=DATE:
      const key = rawKey.split(";")[0];

      switch (key) {
        case "SUMMARY":
          currentEvent.summary = value;
          break;
        case "DTSTART":
          currentEvent.start = formatICalDate(value);
          break;
        case "DTEND":
          currentEvent.end = formatICalDate(value);
          break;
        case "LOCATION":
          currentEvent.location = value;
          break;
        case "DESCRIPTION":
          currentEvent.description = value;
          break;
        case "URL":
          currentEvent.url = value;
          break;
      }
    }
  }

  return events;
};

const formatDescription = (text) => {
  if (!text) return "";
  // Replace literal "\n" and also handle escaped backslashes
  return text
    .replace(/\\n/g, "<br>")
    .replace(/\n/g, "<br>")
    .replace(/\\\\/g, "\\"); // just in case ICS escaped slashes
};

/**
 * Formats iCal date strings into readable form
 * Handles both all-day (YYYYMMDD) and date-time (YYYYMMDDTHHmmssZ)
 */
const formatICalDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    // All-day event
    if (dateStr.length === 8) {
      return `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`;
    }
    // Date-time event
    const [date, time] = dateStr.replace("Z", "").split("T");
    return `${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)} ${time.slice(0, 2)}:${time.slice(2, 4)}`;
  } catch {
    return dateStr;
  }
};

// Parse iCal Blob
const parseIcsBlob = async (blob) => {
  if (!blob) return;
  try {
    const icsText = await blob.text();
    icalData.value = parseICS(icsText);
  } catch (error) {
    console.error("Error parsing iCal Blob:", error);
  }
};

// Reactively update when document changes
watchEffect(() => {
  const ical = store.getters.getDocumentById(props.documentName);
  if (ical?.FileBytes) {
    parseIcsBlob(ical.FileBytes);
  }
});
</script>

<style scoped>
.ical-viewer_container {
  padding: 1rem;
  overflow-y: auto;
  background: #f9f9f9;
  display: flex;
  justify-content: flex-start; /* Align cards to the left */
}

.ical-cards {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: flex-start; /* Ensure cards align left */
  width: 100%;
  max-width: 600px; /* Limit overall width */
}

.ical-card {
  background-color: #fff;
  border-radius: 8px;
  padding: 2.25rem 2.5rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  width: 100%; /* Makes each card the width of .ical-cards */
}

.ical-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
}

.ical-summary {
  margin-bottom: 0.5rem;
  color: #2c3e50;
  font-size: 1.25rem;
}

p {
  margin: 0.25rem 0;
  color: #333;
  line-height: 1.4;
  text-align: left;
}

strong {
  color: #555;
}

a {
  color: #3498db;
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}
</style>
